import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Lightbulb, ThumbsUp, Send } from 'lucide-react';

export const Improvements = ({ user }) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Neue Funktion');
  const [priority, setPriority] = useState('Mittel');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const stats = [
    { label: 'Vorschläge', value: 127, color: '#F59E0B', bgColor: '#FEF3C7', icon: Lightbulb },
    { label: 'Umgesetzt', value: 45, color: '#10B981', bgColor: '#D1FAE5', icon: ThumbsUp },
    { label: 'In Bearbeitung', value: 12, color: '#3B82F6', bgColor: '#DBEAFE', icon: Send }
  ];

  const popularSuggestions = [
    {
      title: 'Dark Mode hinzufügen',
      votes: 24,
      status: 'In Planung',
      statusColor: '#F59E0B'
    },
    {
      title: 'Export als PDF verbessern',
      votes: 18,
      status: 'In Entwicklung',
      statusColor: '#3B82F6'
    },
    {
      title: 'Mobile App',
      votes: 42,
      status: 'Geplant',
      statusColor: '#6366F1'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${getBackendUrl()}/api/improvements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ title, category, priority, description })
      });

      if (response.ok) {
        toast.success('✅ Vorschlag eingereicht!');
        setTitle('');
        setDescription('');
      } else {
        toast.error('❌ Fehler beim Einreichen');
      }
    } catch (error) {
      toast.error('❌ Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="w-full max-w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Verbesserungsvorschläge</h1>
          <p className="text-sm text-gray-600 mt-1">Helfen Sie uns, die Software zu verbessern</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div key={idx} className="bg-white rounded-lg border p-6 shadow-sm" style={{ borderColor: '#E1E4E8' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: stat.bgColor }}>
                    <Icon className="w-6 h-6" style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* New Suggestion Form */}
          <div className="bg-white rounded-lg shadow-sm border p-6" style={{ borderColor: '#E1E4E8' }}>
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Neuer Vorschlag</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Kurze Beschreibung Ihrer Idee"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                  data-testid="suggestion-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="suggestion-category-select"
                >
                  <option>Neue Funktion</option>
                  <option>Verbesserung</option>
                  <option>Fehlerbehebung</option>
                  <option>Design</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priorität</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="suggestion-priority-select"
                >
                  <option>Niedrig</option>
                  <option>Mittel</option>
                  <option>Hoch</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Detaillierte Beschreibung Ihres Vorschlags..."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                  required
                  data-testid="suggestion-description-textarea"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-medium rounded-lg transition-colors"
                style={{ backgroundColor: '#3B82F6' }}
                data-testid="submit-suggestion-button"
              >
                <Send className="w-4 h-4" />
                {loading ? 'Wird eingereicht...' : 'Vorschlag einreichen'}
              </button>
            </form>
          </div>

          {/* Popular Suggestions */}
          <div className="bg-white rounded-lg shadow-sm border p-6" style={{ borderColor: '#E1E4E8' }}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Beliebte Vorschläge</h2>

            <div className="space-y-4">
              {popularSuggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  style={{ borderColor: '#E1E4E8' }}
                  data-testid={`popular-suggestion-${idx}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{suggestion.title}</h3>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <ThumbsUp className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <ThumbsUp className="w-3 h-3" />
                      <span>{suggestion.votes} Stimmen</span>
                    </div>
                    <span
                      className="px-3 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: suggestion.statusColor }}
                    >
                      {suggestion.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Improvements;
